// gfxCanvas.js
// Draws a graphic look ("Text runt bilden") to a canvas from ONE layout: positions are fractions of the
// canvas, the type size a fraction of its width. The same call makes the thumbnail (small width) and the
// print version (large width), so the two cannot drift apart. The original upload and its _copy.jpg are
// never touched; the result is a separate file. Layout values follow the designer's sketch (4a/4b).

export const AROUND = {
    ratio: 5 / 7,
    image: { left: 0.165, right: 0.176, top: 0.13, bottom: 0.142 }, // insets as fractions of width/height
    fontSize: 0.07, // of width (7cqw in the sketch)
    top: { x: 0.145, baselineFromBottom: 0.87 }, // y of the baseline as fraction of height from the top: 1 - 0.87
    left: { x: 0.165, y0: 0.81, y1: 0.16 }, // reads bottom-to-top, starts at y0
    right: { x: 0.824, y0: 0.145, y1: 0.858 }, // reads top-to-bottom, starts at y0
    limits: { left: 26, top: 20, right: 30 },
};

function drawGray(ctx, img, x, y, w, h) {
    // object-fit: cover
    const s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    const sw = w / s, sh = h / s;
    const sx = (img.naturalWidth - sw) / 2, sy = (img.naturalHeight - sh) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    if ('filter' in ctx) {
        ctx.filter = 'grayscale(1)';
        ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
    } else {
        // Safari: no canvas filter, convert the pixels
        ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
        const d = ctx.getImageData(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
        for (let i = 0; i < d.data.length; i += 4) {
            const g = 0.299 * d.data[i] + 0.587 * d.data[i + 1] + 0.114 * d.data[i + 2];
            d.data[i] = d.data[i + 1] = d.data[i + 2] = g;
        }
        ctx.putImageData(d, Math.round(x), Math.round(y));
    }
    ctx.restore();
}

/**
 * @param {Object} o
 * @param {HTMLImageElement} o.image  loaded image
 * @param {number} o.width            canvas width in px (height follows the 5:7 ratio)
 * @param {string} o.left,o.top,o.right  the three texts (cut to the limits)
 * @param {string} o.font             CSS font-family; the font must already be loaded (document.fonts.load)
 * @param {string} o.ink              text colour
 * @param {string} [o.paper]          background colour
 * @returns {HTMLCanvasElement}
 */
export function renderAround(o) {
    const L = AROUND;
    const W = Math.round(o.width), H = Math.round(W / L.ratio);
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d');
    ctx.fillStyle = o.paper || '#fff';
    ctx.fillRect(0, 0, W, H);

    const ix = W * L.image.left, iy = H * L.image.top;
    drawGray(ctx, o.image, ix, iy, W - ix - W * L.image.right, H - iy - H * L.image.bottom);

    const size = W * L.fontSize;
    ctx.fillStyle = o.ink;
    ctx.strokeStyle = o.ink;
    ctx.lineWidth = size * 0.025;
    ctx.font = size + 'px ' + o.font;
    ctx.textBaseline = 'alphabetic';
    const put = (text, x, y, angle, ghost) => {
        ctx.save();
        if (ghost) ctx.globalAlpha = 0.3;
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.fillText(text, 0, 0);
        ctx.strokeText(text, 0, 0);
        ctx.restore();
    };
    put(String(o.top || '').slice(0, L.limits.top), W * L.top.x, H * (1 - L.top.baselineFromBottom) - size * 0.1, 0, o.ghost?.top);
    put(String(o.left || '').slice(0, L.limits.left), W * L.left.x - size * 0.2, H * L.left.y0, -Math.PI / 2, o.ghost?.left);
    put(String(o.right || '').slice(0, L.limits.right), W * L.right.x + size * 0.8, H * L.right.y0, Math.PI / 2, o.ghost?.right);
    return c;
}

export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
    return new Promise((res) => canvas.toBlob(res, type, quality));
}

// ---- Handskriven text -------------------------------------------------------------------------------

export const VOW = {
    ratio: 4 / 5,
    paper: { left: 0.19, right: 0.19, top: 0.152, bottom: 0.152 }, // the sheet inside the canvas
    fontSize: 0.033, // of width (3.3cqw in the sketch)
    limits: { vow: 360, sign: 32 },
};

const rnd = (i) => {
    const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
};

/**
 * @param {Object} o  image, width, vow (the text), sign, font, ink, paper
 * @returns {HTMLCanvasElement}
 */
export function renderVow(o) {
    const L = VOW;
    const W = Math.round(o.width), H = Math.round(W / L.ratio);
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d');
    ctx.fillStyle = o.paper || '#faf9f7';
    ctx.fillRect(0, 0, W, H);

    // the sheet
    const sx = W * L.paper.left, sy = H * L.paper.top;
    const sw = W - sx - W * L.paper.right, sh = H - sy - H * L.paper.bottom;
    ctx.fillStyle = '#f8f6f2';
    ctx.fillRect(sx, sy, sw, sh);

    // the image: 58% of the sheet width, 4:5, 6% of the canvas width below the top edge
    const iw = sw * 0.58, ih = iw * 1.25;
    const ix = sx + (sw - iw) / 2, iy = sy + W * 0.06;
    drawCover(ctx, o.image, ix, iy, iw, ih);

    // the words, with the small per-word offsets of the sketch
    const size = W * L.fontSize;
    ctx.fillStyle = o.ink;
    ctx.font = size + 'px ' + o.font;
    ctx.textBaseline = 'alphabetic';
    const colLeft = ix - iw * 0.09, colW = iw * 1.18;
    let x = colLeft, y = iy + ih + W * 0.035 + size;
    const lineH = size * 1.05;
    const words = String(o.vow || '').slice(0, L.limits.vow).split(/\s+/).filter(Boolean);
    const bottom = sy + sh - W * 0.04 - size * 1.6; // keep room for the signature
    words.forEach((word, i) => {
        const gap = (0.2 + rnd(i + 7) * 0.14) * size;
        const w = ctx.measureText(word).width;
        if (x + w > colLeft + colW && x > colLeft) {
            x = colLeft;
            y += lineH;
        }
        if (y > bottom) return; // clipped like the sheet's overflow
        ctx.save();
        ctx.globalAlpha = (0.88 + rnd(i + 23) * 0.12) * (o.ghost?.vow ? 0.3 : 1);
        ctx.translate(x, y + (Math.sin(i * 0.55) * 0.07 + (rnd(i) - 0.5) * 0.1 - i * 0.004) * size);
        ctx.rotate(((rnd(i + 50) - 0.5) * 3.2 * Math.PI) / 180);
        const sc = 0.95 + rnd(i + 99) * 0.1;
        ctx.scale(sc, sc);
        ctx.fillText(word, 0, 0);
        ctx.restore();
        x += w + gap;
    });

    // the signature, bottom right, slightly tilted
    const sign = String(o.sign || '').slice(0, L.limits.sign);
    ctx.save();
    if (o.ghost?.sign) ctx.globalAlpha = 0.3;
    ctx.font = size + 'px ' + o.font;
    ctx.translate(ix + iw - ctx.measureText(sign).width, sy + sh - W * 0.04);
    ctx.rotate((-1.5 * Math.PI) / 180);
    ctx.fillText(sign, 0, 0);
    ctx.restore();
    return c;
}

function drawCover(ctx, img, x, y, w, h) {
    const s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    const sw = w / s, sh = h / s;
    ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, x, y, w, h);
}

// ---- Registry ---------------------------------------------------------------------------------------

/** presentation slug (pa_presentation) -> graphic kind */
export const GFX_PRESENTATIONS = { 'text-around': 'around', 'text-vow': 'vow' };

export const GFX_INK = [
    { v: 'ink', label: 'Bläck', c: '#262a3a' },
    { v: 'black', label: 'Svart', c: '#1d1d1b' },
    { v: 'red', label: 'Röd', c: '#a52832' },
    { v: 'blue', label: 'Blå', c: '#1f4e9c' },
];

/** Text fields per kind: key, label, default (sketch), max characters. */
export const GFX_FIELDS = {
    around: [
        { k: 'left', label: 'Vänster sida, nedifrån', def: 'Första sommaren, juli 2026', max: 26 },
        { k: 'top', label: 'Överkant', def: '♡ Anna & Johan', max: 20 },
        { k: 'right', label: 'Höger sida, uppifrån', def: 'Böda strand, Öland', max: 30 },
    ],
    vow: [
        { k: 'vow', label: 'Din text, till exempel ett bröllopslöfte', def: 'Jag lovar att vara din, i det stora och i det lilla. Att skratta med dig när det är lätt och stå kvar när det är svårt. Att laga frukost på söndagar och att hålla din hand på väg hem. Att välja dig, varje dag, resten av livet.', max: 360, area: true },
        { k: 'sign', label: 'Signatur och datum', def: 'Anna, 15 augusti 2026', max: 32 },
    ],
};

export const GFX_DEFAULT_INK = { around: 'black', vow: 'ink' };
export const GFX_FONT = "'Cedarville Cursive', cursive"; // typeface not decided yet: browser cursive until the files arrive

export function renderGraphic(kind, o) {
    return kind === 'vow' ? renderVow(o) : renderAround(o);
}
